import dayjs from 'dayjs';
import { Info } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';

import {
  CAMPAIGN_POSTS_SORT,
  CampaignStatus,
  useCampaignPostCounts,
  useCampaignPosts,
  useExcludeCampaignPostsMutation,
  type Campaign,
  type CampaignPostListItem,
} from '@/entities/campaign';
import { SOURCE_PLATFORM_TYPE } from '@/entities/public-filter/constants';
import { SocialPostsGrid } from '@/features/(insight)/posts/ui/social-post-card/social-posts-grid';
import type {
  CampaignDashboardPost,
  PostFilterOption,
  PostTabFilterRequest,
} from '@/features/campaign/ui/campaign-detail/hooks/campaign-detail-state-types';
import { PageNavigationView } from '@/shared/components/page-navigation/page-navigation';
import { Alert, AlertDescription } from '@/shared/components/shadcn/alert';
import { Badge } from '@/shared/components/shadcn/badge';
import { Button } from '@/shared/components/shadcn/button';
import { Checkbox } from '@/shared/components/shadcn/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/components/shadcn/select';
import {
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/shared/components/shadcn/table';
import { TooltipFlat } from '@/shared/components/shadcn/tooltip/tooltip-flat';
import { Stage } from '@/shared/components/stage/stage';
import { useWorkspace } from '@/shared/hooks/use-workspace';
import { formatCompactPercent, formatNumber } from '@/shared/utils/format-number';
import { formatRelativeTime } from '@/shared/utils/format-relative-time';
import { requestConfirm } from '@/shared/utils/request-confirm';

import { showProfilePopup } from '@/features/(insight)/shared/profile-popup';
import { formatDateTime } from '@/shared/utils/date/date-formats';
import { Link } from '@tanstack/react-router';
import {
  POSTS_PAGE_SIZE,
  POST_ER_FORMULA,
  RECENT_POSTS_TOOLTIP,
  TOTAL_ENGAGEMENTS_FORMULA,
  postSortOptions,
  postViewModeOptions,
} from '../../constants';
import {
  CampaignDataTable,
  EmptyInlineState,
  FilterPills,
  ViewModeToggle,
} from '../campaign-detail-primitives';
import { useCampaignDetailPostTabState } from './use-campaign-detail-post-tab-state';
import { CreatorProfile } from '@/features/creator/ui/creator-profile/creator-profile';

type CampaignDetailPostTabProps = {
  campaignId: string;
  campaign: Campaign;
  filterRequest: PostTabFilterRequest | null;
  isActive: boolean;
  onOpenRequirementEditor: () => void;
};

export function CampaignDetailPostTab({
  campaignId,
  campaign,
  filterRequest,
  isActive,
  onOpenRequirementEditor,
}: CampaignDetailPostTabProps) {
  const { workspace } = useWorkspace();
  const postTabState = useCampaignDetailPostTabState();
  const { setPostFilter } = postTabState;
  const [selectedPostIds, setSelectedPostIds] = useState<string[]>([]);
  const [cardPostsPageCount, setCardPostsPageCount] = useState(1);
  const isListView = postTabState.postViewMode === 'list';
  const slotCapacity = workspace?.constraints.campaign_item_slot_limit ?? 0;
  const canEdit = campaign.status !== CampaignStatus.ENDED;
  const isEnded = campaign.status === CampaignStatus.ENDED;
  const isScheduled = campaign.status === CampaignStatus.SCHEDULED;

  const postRequest = useMemo(
    () => ({
      sort: postTabState.postSort,
      page: isListView ? postTabState.postPage : 1,
      limit: POSTS_PAGE_SIZE,
    }),
    [isListView, postTabState.postPage, postTabState.postSort],
  );
  const postCountsQuery = useCampaignPostCounts(campaignId);
  const postsQuery = useCampaignPosts(campaignId, postTabState.isRecentPosts, postRequest, {
    mode: isListView ? 'page' : 'accumulate',
    pageCount: cardPostsPageCount,
  });
  const posts = postsQuery.items;
  const totalCount = postsQuery.totalCount;
  const isLoading = postsQuery.isLoading;
  const isError = postsQuery.isError;

  const recentCount = postCountsQuery.data?.recent ?? 0;
  const allCount = postCountsQuery.data?.total ?? 0;

  const visiblePostIdsKey = useMemo(
    () =>
      posts
        .map((post) => post.campaign_post_id)
        .sort()
        .join('|'),
    [posts],
  );
  const postFilterPillOptions = useMemo(
    () => [
      {
        label: 'All posts',
        value: 'all' as const,
        count: allCount,
        isCountLoading:
          postCountsQuery.totalQuery.isLoading || postCountsQuery.totalQuery.isFetching,
      },
      {
        label: 'Recently collected',
        value: 'recent' as const,
        count: recentCount,
        isCountLoading:
          postCountsQuery.recentQuery.isLoading || postCountsQuery.recentQuery.isFetching,
        tooltip: RECENT_POSTS_TOOLTIP,
      },
    ],
    [
      allCount,
      postCountsQuery.recentQuery.isFetching,
      postCountsQuery.recentQuery.isLoading,
      postCountsQuery.totalQuery.isFetching,
      postCountsQuery.totalQuery.isLoading,
      recentCount,
    ],
  );
  const totalPages = Math.max(1, Math.ceil(totalCount / POSTS_PAGE_SIZE));
  const shouldPaginate = postTabState.postViewMode === 'list' && totalCount > POSTS_PAGE_SIZE;
  const pageStartIndex = (postTabState.postPage - 1) * POSTS_PAGE_SIZE;
  const excludePostsMutation = useExcludeCampaignPostsMutation();

  useEffect(() => {
    if (!filterRequest) return;

    setPostFilter(filterRequest.filter);
  }, [filterRequest, setPostFilter]);

  useEffect(() => {
    setCardPostsPageCount(1);
  }, [postTabState.postFilter, postTabState.postSort, postTabState.postViewMode]);

  useEffect(() => {
    setSelectedPostIds([]);
  }, [
    postTabState.postFilter,
    postTabState.postPage,
    postTabState.postSort,
    postTabState.postViewMode,
    visiblePostIdsKey,
  ]);

  useEffect(() => {
    if (isActive) return;

    setSelectedPostIds([]);
  }, [isActive]);

  const toggleSelectedPostById = (postId: string) => {
    setSelectedPostIds((current) =>
      current.includes(postId) ? current.filter((id) => id !== postId) : [...current, postId],
    );
  };

  const clearSelectedPosts = () => {
    setSelectedPostIds([]);
  };

  const handleRemoveSelected = async () => {
    if (selectedPostIds.length === 0) return;

    try {
      await excludePostsMutation.mutateAsync({ campaignId, postIds: selectedPostIds });
      setSelectedPostIds([]);
      toast.success(
        selectedPostIds.length === 1
          ? 'Post deleted from campaign results.'
          : `${selectedPostIds.length} posts deleted from campaign results.`,
      );
    } catch {
      toast.error('Failed to delete posts. Please try again.');
    }
  };

  const handleRetry = () => {
    void postCountsQuery.refetch();
    void postsQuery.refetch();
  };

  const handleRequestExclude = async () => {
    await requestConfirm({
      title: selectedPostIds.length === 1 ? 'Delete selected post?' : 'Delete selected posts?',
      description:
        selectedPostIds.length === 1
          ? 'This post will be removed from campaign results.'
          : `${selectedPostIds.length} posts will be removed from campaign results.`,
      confirmButton: {
        children: selectedPostIds.length === 1 ? 'Delete post' : 'Delete posts',
        variant: 'destructive',
      },
      onConfirm: async () => {
        await handleRemoveSelected();
      },
    });
  };

  const toggleAll = () => {
    if (selectedPostIds.length === posts.length) {
      setSelectedPostIds([]);
    } else {
      setSelectedPostIds(posts.map((post) => post.campaign_post_id));
    }
  };

  return (
    <div className="flex flex-col gap-6">
      <section className="space-y-4">
        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-2 lg:flex-row lg:items-center lg:justify-between">
            <div>
              <h2 className="text-foreground text-xl leading-6 font-semibold">Post</h2>
            </div>
            <Button
              size="sm"
              variant="outline"
              disabled={!canEdit}
              onClick={onOpenRequirementEditor}
            >
              Edit Post URL
            </Button>
          </div>

          <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
            <FilterPills
              options={postFilterPillOptions}
              value={postTabState.postFilter}
              onChange={(value) => postTabState.setPostFilter(value as PostFilterOption)}
            />
            <div className="flex flex-wrap items-center gap-2 lg:justify-end">
              <ViewModeToggle
                options={postViewModeOptions}
                value={postTabState.postViewMode}
                onChange={(value) => postTabState.setPostViewMode(value as 'card' | 'list')}
              />
              <div className="flex items-center gap-2">
                <span className="text-muted-foreground text-sm">Sort by</span>
                <Select
                  value={postTabState.postSort}
                  onValueChange={(value) => postTabState.setPostSort(value as CAMPAIGN_POSTS_SORT)}
                >
                  <SelectTrigger className="w-full sm:w-[180px]">
                    <SelectValue placeholder="Sort posts" />
                  </SelectTrigger>
                  <SelectContent>
                    {postSortOptions.map((option) => (
                      <SelectItem
                        key={option.value}
                        value={option.value}
                        aria-label={
                          option.helper ? `${option.label}: ${option.helper}` : option.label
                        }
                      >
                        <span className="flex items-center gap-1.5">
                          <span>{option.label}</span>
                          {option.helper ? (
                            <TooltipFlat
                              asChild
                              side="right"
                              contentClassName="max-w-56"
                              content={option.helper}
                            >
                              <span
                                className="text-muted-foreground inline-flex cursor-pointer"
                                aria-hidden="true"
                              >
                                <Info className="size-3.5" />
                              </span>
                            </TooltipFlat>
                          ) : null}
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
        </div>

        <PostTrackingInfoStrip
          isEnded={isEnded}
          isScheduled={isScheduled}
          lastSyncedAt={campaign.updated_at}
          scheduledStartDate={campaign.start_date}
          slotCapacity={slotCapacity}
          usedSlots={allCount}
        />

        <Stage>
          <Stage.Scene status={isError}>
            <EmptyInlineState
              title="Posts are unavailable"
              description="We could not load tracked posts right now."
              action={
                <Button variant="outline" size="sm" onClick={handleRetry}>
                  Retry
                </Button>
              }
            />
          </Stage.Scene>
          <Stage.Scene status={isLoading}>
            <EmptyInlineState
              title="Loading posts"
              description="Fetching the latest tracked posts for this campaign."
            />
          </Stage.Scene>
          <Stage.Scene status={posts.length === 0}>
            <EmptyInlineState
              title="No posts in this view"
              description="Switch filters or add a post to start building the campaign post set."
            />
          </Stage.Scene>
          <Stage.Scene status={postTabState.postViewMode === 'card'}>
            <SocialPostsGrid<CampaignPostListItem>
              className="p-0"
              posts={posts.map((p) => {
                return {
                  ...p.post,
                  label: p.label,
                  campaign_post_id: p.campaign_post_id,
                };
              })}
              selectedPostIds={selectedPostIds}
              onTogglePostSelect={(item) => {
                toggleSelectedPostById(item.campaign_post_id);
              }}
              getPostSelectionId={(item) => item.campaign_post_id}
              hasNextPage={postsQuery.hasNextPage}
              isFetchingNextPage={postsQuery.isFetchingNextPage}
              onLoadMore={() => {
                if (postsQuery.hasNextPage && !postsQuery.isFetchingNextPage) {
                  setCardPostsPageCount((pageCount) => pageCount + 1);
                }
              }}
              postFooter={{
                label: 'Updated',
                date: 'updated_at',
              }}
            />
          </Stage.Scene>
          <Stage.Scene default>
            <CampaignPostTableView
              pageSize={POSTS_PAGE_SIZE}
              posts={posts}
              currentPage={postTabState.postPage}
              selectedPostIds={selectedPostIds}
              onToggleAll={toggleAll}
              totalCount={totalCount}
              totalPages={totalPages}
              onNextPage={() => postTabState.setPostPage((page) => Math.min(page + 1, totalPages))}
              onPreviousPage={() => postTabState.setPostPage((page) => Math.max(page - 1, 1))}
              onTogglePost={toggleSelectedPostById}
            />
          </Stage.Scene>
        </Stage>

        {!isError && !isLoading && shouldPaginate ? (
          <PageNavigationView
            className="justify-end px-0 py-0"
            startIndex={pageStartIndex + 1}
            endIndex={Math.min(pageStartIndex + posts.length, totalCount)}
            totalCount={totalCount}
            onPrevious={() => postTabState.setPostPage((page) => Math.max(page - 1, 1))}
            onNext={() => postTabState.setPostPage((page) => Math.min(page + 1, totalPages))}
            canGoPrevious={postTabState.postPage > 1}
            canGoNext={postTabState.postPage < totalPages}
          />
        ) : null}

        <CampaignPostSelectionActionBar
          selectedCount={selectedPostIds.length}
          onClear={clearSelectedPosts}
          onConfirmExclude={() => void handleRequestExclude()}
        />
      </section>
    </div>
  );
}

function CampaignPostTableView({
  onToggleAll,
  onNextPage,
  onTogglePost,
  onPreviousPage,
  currentPage,
  pageSize,
  posts,
  selectedPostIds,
  totalCount,
  totalPages,
}: {
  onToggleAll: () => void;
  onNextPage: () => void;
  onTogglePost: (postId: string) => void;
  onPreviousPage: () => void;
  currentPage: number;
  pageSize: number;
  posts: CampaignDashboardPost[];
  selectedPostIds: string[];
  totalCount: number;
  totalPages: number;
}) {
  const allSelected = selectedPostIds.length === posts.length;
  const someSelected = selectedPostIds.length > 0 && selectedPostIds.length < posts.length;
  const startIndex = (currentPage - 1) * pageSize + 1;
  const endIndex = Math.min(startIndex + posts.length - 1, totalCount);
  const postTableGridTemplateColumns =
    '44px 74px 200px minmax(240px,1.5fr) minmax(120px,0.9fr) minmax(120px,0.9fr) minmax(110px,0.8fr) minmax(110px,0.8fr) minmax(110px,0.8fr) minmax(110px,0.8fr) minmax(190px,1.1fr) minmax(130px,0.8fr) minmax(120px,0.9fr)';

  return (
    <CampaignDataTable
      footer={
        totalCount > pageSize ? (
          <PageNavigationView
            className="p-0"
            startIndex={startIndex}
            endIndex={endIndex}
            totalCount={totalCount}
            onPrevious={onPreviousPage}
            onNext={onNextPage}
            canGoPrevious={currentPage > 1}
            canGoNext={currentPage < totalPages}
          />
        ) : null
      }
    >
      <TableHeader>
        <TableRow gridTemplateColumns={postTableGridTemplateColumns}>
          <TableHead className="justify-center">
            <Checkbox
              checked={allSelected ? true : someSelected ? 'indeterminate' : false}
              onCheckedChange={onToggleAll}
              aria-label="Select all visible posts"
            />
          </TableHead>
          <TableHead>Post</TableHead>
          <TableHead>Content</TableHead>
          <TableHead>Creator</TableHead>
          <TableHead>Label</TableHead>
          <TableHead>Posted</TableHead>
          <TableHead>Views</TableHead>
          <TableHead>Likes</TableHead>
          <TableHead>Comments</TableHead>
          <TableHead>Shares</TableHead>
          <TableHead>
            <span className="flex items-center gap-1.5">
              <span>Total Engagements</span>
              <TooltipFlat
                asChild
                side="top"
                contentClassName="max-w-56"
                content={TOTAL_ENGAGEMENTS_FORMULA}
              >
                <button
                  type="button"
                  className="text-muted-foreground hover:text-foreground cursor-pointer transition-colors"
                  aria-label={TOTAL_ENGAGEMENTS_FORMULA}
                >
                  <Info className="size-3.5" />
                </button>
              </TooltipFlat>
            </span>
          </TableHead>
          <TableHead>
            <span className="flex items-center gap-1.5">
              <span>Post ER</span>
              <TooltipFlat asChild side="top" contentClassName="max-w-56" content={POST_ER_FORMULA}>
                <button
                  type="button"
                  className="text-muted-foreground hover:text-foreground cursor-pointer transition-colors"
                  aria-label={POST_ER_FORMULA}
                >
                  <Info className="size-3.5" />
                </button>
              </TooltipFlat>
            </span>
          </TableHead>
          <TableHead>Updated</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {posts.map((campaignPost) => {
          const post = campaignPost.post;
          const isSelected = selectedPostIds.includes(campaignPost.campaign_post_id);

          const totalEngagements =
            post.extraction.engagement.like +
            post.extraction.engagement.shared +
            post.extraction.engagement.comment;

          return (
            <TableRow
              key={campaignPost.campaign_post_id}
              gridTemplateColumns={postTableGridTemplateColumns}
            >
              <TableCell className="justify-center">
                <Checkbox
                  checked={isSelected}
                  onCheckedChange={() => onTogglePost(campaignPost.campaign_post_id)}
                  aria-label={`Select post by ${post.author.nickname}`}
                />
              </TableCell>
              <TableCell className="items-center">
                <Link
                  to="/workspace/$workspaceId/post/$postId"
                  params={{
                    postId: post._id,
                  }}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <img src={post.extraction.video?.poster_url} className="w-10 rounded" />
                </Link>
              </TableCell>
              <TableCell className="items-center whitespace-normal">
                <TooltipFlat
                  content={
                    <p className="max-w-100 min-w-0 text-sm break-all">
                      {post.extraction.content || '-'}
                    </p>
                  }
                >
                  <p className="line-clamp-2 min-w-0 text-sm break-all">
                    {post.extraction.content || '-'}
                  </p>
                </TooltipFlat>
              </TableCell>
              <TableCell className="items-center">
                <CreatorProfile
                  username={post.author.username}
                  displayName={post.author.nickname}
                  avatar={post.author.profile_image_url ?? ''}
                  platform={post.source.platform}
                  onProfileClick={async (e: React.MouseEvent) => {
                    e.stopPropagation();
                    const { author } = post;
                    await showProfilePopup({
                      position: { x: e.clientX, y: e.clientY },
                      profileImageUrl: author.profile_image_url,
                      username: author.username,
                      nickname: author.nickname,
                      followerCount: author.follower_count,
                      profileUrl: author.profile_url,
                    });
                  }}
                />
              </TableCell>
              <TableCell>
                {campaignPost.label ? <Badge variant="secondary">{campaignPost.label}</Badge> : '—'}
              </TableCell>
              <TableCell>{dayjs(post.date).format('MMM D')}</TableCell>
              <TableCell>{formatNumber(post.extraction.engagement.view)}</TableCell>
              <TableCell>{formatNumber(post.extraction.engagement.like)}</TableCell>
              <TableCell>{formatNumber(post.extraction.engagement.comment)}</TableCell>
              <TableCell>
                {post.source.platform === SOURCE_PLATFORM_TYPE.TIKTOK
                  ? formatNumber(post.extraction.engagement.shared)
                  : '—'}
              </TableCell>
              <TableCell>{formatNumber(totalEngagements)}</TableCell>
              <TableCell>{formatCompactPercent(post.extraction.engagement.rate * 100)}</TableCell>
              <TableCell>
                <TooltipFlat
                  asChild
                  side="top"
                  content={`Metrics updated ${formatDateTime(post.updated_at)}`}
                >
                  <span className="text-muted-foreground cursor-default text-sm">
                    {formatRelativeTime(post.updated_at)}
                  </span>
                </TooltipFlat>
              </TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </CampaignDataTable>
  );
}

function CampaignPostSelectionActionBar({
  onConfirmExclude,
  selectedCount,
  onClear,
}: {
  onConfirmExclude: () => void;
  selectedCount: number;
  onClear: () => void;
}) {
  if (selectedCount === 0) return null;

  return (
    <div className="pointer-events-none fixed right-0 bottom-6 left-0 z-40 flex justify-center px-4">
      <div className="border-border bg-background/95 pointer-events-auto flex items-center gap-3 rounded-full border px-4 py-3 shadow-lg backdrop-blur">
        <span className="text-sm font-medium">{selectedCount} selected</span>
        <Button size="sm" onClick={onConfirmExclude}>
          Delete selected
        </Button>
        <Button size="sm" variant="outline" onClick={onClear}>
          Clear
        </Button>
      </div>
    </div>
  );
}

function PostTrackingInfoStrip({
  isEnded,
  isScheduled,
  lastSyncedAt,
  scheduledStartDate,
  slotCapacity,
  usedSlots,
}: {
  isEnded: boolean;
  isScheduled: boolean;
  lastSyncedAt?: string;
  scheduledStartDate: string;
  slotCapacity: number;
  usedSlots: number;
}) {
  return (
    <Alert className="border-border bg-muted/70 px-4 py-3">
      <AlertDescription className="flex flex-col gap-1.5 sm:flex-row sm:items-center sm:justify-between">
        <div className="min-w-0">
          <p className="text-foreground text-sm font-medium">
            {isEnded
              ? 'Tracking has ended'
              : isScheduled
                ? 'Tracking has not started yet'
                : 'Daily tracking is active'}
          </p>
          <p className="text-muted-foreground text-sm">
            {isEnded
              ? 'New posts are no longer collected and metrics are no longer updated.'
              : isScheduled
                ? `Tracking starts on ${dayjs(scheduledStartDate).format('MMM D, YYYY')}. New posts and metrics will appear after kickoff.`
                : 'We discover new posts daily and keep metrics updated.'}
          </p>
        </div>
        <div className="text-muted-foreground flex shrink-0 flex-wrap items-center gap-x-3 gap-y-1 text-xs">
          {!isScheduled && lastSyncedAt && (
            <>
              <span>Last sync {formatDateTime(lastSyncedAt)}</span>
              <span className="text-border hidden sm:inline">•</span>
            </>
          )}
          <span>
            {formatNumber(usedSlots)} / {formatNumber(slotCapacity)} slots used
          </span>
        </div>
      </AlertDescription>
    </Alert>
  );
}
